import { Box, Stack, Typography } from '@mui/material';
import { AdvancedMarker, GeoLocationMap } from '@parsys/frontend_library';
import dayjs from 'dayjs';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ErrorFallback from '../ErrorFallback';
import AmbulanceIcon from './AmbulanceIcon/AmbulanceIcon';
import CenterMapButton from './CenterMapButton/CenterMapButton';
import { getIconColorByStatus } from './utils';
import Loader from '../Loader/Loader';
import { pickItem, selectAllMarkers, setCurrentCenter, setLastCenter, setZoom, unpickItem } from 'FRONTEND/reducers/geolocationSlice';
import { listSmurTeams } from 'FRONTEND/reducers/smurTeamSlice';
import i18n from 'FRONTEND/utils/i18n';
import throttle from 'FRONTEND/utils/throttle';

/**
 * Render callback for the GeoLocationMap component. It displays a loader when the map is loading, an error message when the map fails to load, and the map itself when it is loaded.
 */
const renderCallback = (status) => {
  if (status === 'FAILURE') {
    return <ErrorFallback />;
  }
  return <Loader position='fullScreen' isLoading={status !== 'SUCCESS'} size='large' />;
};

/**
 * Displays a map with markers for SMUR teams.
 */
const Geolocation = () => {
  const dispatch = useDispatch();
  const selectedItem = useSelector((state) => state.geolocation.pickedObjectId);
  const zoom = useSelector((state) => state.geolocation.currentZoom);
  const currentCenter = useSelector((state) => state.geolocation.currentCenter);
  const googleMapsAPIKey = useSelector((state) => state.geolocation.googleMapsAPIKey);
  useEffect(() => {
    dispatch(listSmurTeams());
    const pollingSmurTeamsInterval = setInterval(() => dispatch(listSmurTeams()), 20000);
    const previousCenter = localStorage.getItem('lastCurrentLocation');
    if (previousCenter) {
      dispatch(setCurrentCenter({ position: JSON.parse(previousCenter) }));
    }
    return () => clearInterval(pollingSmurTeamsInterval);
  }, []);
  const throttledSetZoom = useCallback((map) => throttle(
    dispatch(setZoom({ zoom: map?.getZoom() })),
    3000,
  ), []);
  const throttledSetLastCenter = useCallback((map) => throttle(
    dispatch(setLastCenter({ position: { lat: map?.getCenter().lat(), lng: map?.getCenter().lng() } })),
    3000,
  ), []);

  const handleItemClick = (item) => {
    if (item.station.id === selectedItem) {
      dispatch(setZoom({ zoom: zoom - 2 }));
      return dispatch(unpickItem());
    }
    if (item.gmpPosition) {
      dispatch(setCurrentCenter({ position: item.gmpPosition }));
      dispatch(setZoom({ zoom: zoom + 2 }));
    }
    return dispatch(pickItem({ objectId: item.station.id }));
  };
  const markers = useSelector((state) => selectAllMarkers(state));
  const handleMapClick = () => {
    dispatch(unpickItem());
  };
  return (
    <Stack overflow='scroll'>
      {!googleMapsAPIKey ? <Loader isLoading={!googleMapsAPIKey} position='verticalFlow' /> : (
        <GeoLocationMap
          renderCallback={renderCallback}
          googleMapsAPIKey={googleMapsAPIKey}
          mapId='ff4089b0213a6fff'
          center={currentCenter}
          zoom={zoom}
          onCenterChange={throttledSetLastCenter}
          onZoomChange={throttledSetZoom}
          onClickMap={handleMapClick}
          showZoomAndCenterResetButton
          renderCustomZoomAndCenterButton={() => <CenterMapButton />}
          mapContainerStyle={{ flexGrow: '1', height: '95vh' }}

        >
          {[...markers.smurTeams].map((item, index) => {
            return (
              <AdvancedMarker key={index} position={item.gmpPosition} onClick={() => handleItemClick(item)}>
                {item.station.id !== selectedItem && (
                  <Stack
                    alignItems='center' borderRadius='50%' height='40px' width='40px' gap='16px' justifyContent='center' padding={1} position='relative' sx={{
                      backgroundColor: getIconColorByStatus(item),
                      transition: 'all 0.3s ease-out',
                      transform: item.id === selectedItem ? 'scale(1.25)' : '',
                      '&::after': {
                        borderLeft: '8px solid transparent',
                        borderRight: '8px solid transparent',
                        borderTop: `8px solid ${getIconColorByStatus(item)}`,
                        content: '""',
                        left: '50%',
                        height: '50%',
                        width: 0,
                        position: 'absolute',
                        top: '95%',
                        transform: 'translate(-50%,0)',
                        transition: 'all 0.3s ease-out',
                        zIndex: 1,
                      },
                      '&:hover': {
                        transform: 'scale(1.25)',
                      },
                    }}
                  >
                    <AmbulanceIcon sx={{ color: (theme) => theme.palette.common.black }} />
                  </Stack>)}
                <Stack
                  borderRadius='1rem'
                  padding={1}
                  gap={1}
                  display={item.station.id === selectedItem ? 'flex' : 'none'}
                  backgroundColor='white'
                  sx={{
                    '&::after': {
                      borderLeft: '8px solid transparent',
                      borderRight: '8px solid transparent',
                      borderTop: '8px solid white',
                      content: '""',
                      left: '50%',
                      height: '50%',
                      width: 0,
                      position: 'absolute',
                      top: '99%',
                      transform: 'translate(-50%,0)',
                      transition: 'all 0.3s ease-out',
                      zIndex: 1,
                    },
                  }}
                >
                  <Typography variant='h6'>{item.station.alias}</Typography>
                  <Typography>{i18n._('Last known lat/lng ')}<Box sx={{ fontWeight: 'bold' }}>{`${item.gmpPosition.lat}, ${item.gmpPosition.lng}`}</Box></Typography>
                  <Typography>{i18n._('Last updated ')} <Box sx={{ fontWeight: 'bold' }}>{dayjs(item.updated_at).format('HH[h]mm:ss')} </Box></Typography>
                </Stack>
              </AdvancedMarker>
            );
          },
          )}
        </GeoLocationMap>
      )}

      {/* <Stack sx={{ position: 'absolute', gap: 2, bottom: 15, width: '50%', left: '25%', right: '25%', alignItems: 'center', cursor: 'pointer' }}>
        {markers.interventions.map((intervention) => !intervention.seenByOperator ? (
          <Alert
            key={intervention.id}
            severity='info'
            color='warning'
            onClose={() => dispatch(markOneInterventionAsSeen(intervention.id))}
            sx={{ borderRadius: (theme) => theme.spacing(3) }}
          >
            <Box onClick={() => {
              handleItemClick(intervention);
              dispatch(pickItem({ objectId: intervention.id }));
              dispatch(markOneInterventionAsSeen(intervention.id));
            }}
            >
              {i18n._('A new intervention was added, click to center the map on it')}
            </Box>
          </Alert>) : null)} */}
      {/* </Stack> */}
    </Stack>
  );
};

export default Geolocation;
